/**
 * Korean locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesKo = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `취소`,
    delete: `삭제`,
    save: `저장`,
    saveAndReboot: `저장 및 재부팅`,
    actions: `작업`,
    error: `오류`,
    yes: `예`,
    no: `아니오`,
    all: `전체`,
    unchanged: `변경 안함`,
    loading: `로딩 중...`,
    none: `— 없음 —`,
    backToList: `목록으로 돌아가기`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `보통`,
    jitterLow: `낮음`,
    jitterHigh: `높음`,
    natNormal: `보통`,
    natStun: `STUN`,
    serverGroupPrimary: `기본`,
    serverGroupSecondary: `보조`,
    sourceMango: `Mango New`,
    sourceRefurbished: `Mango Refurbished`,
    sourceCustomer: `고객`,
    wifiSecurityNone: `NONE`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `장치 추가`,
    editDevice: `장치 편집`,
    createDevice: `장치 생성`,
    loadingDevice: `장치 로딩 중...`,

    // Section headers
    deviceInfo: `장치 정보`,
    advanced: `고급`,
    displaySettings: `디스플레이 설정`,

    // Field labels
    model: `모델`,
    selectModel: `장치 모델 선택…`,
    mostCommonYealink: `가장 일반적 (Yealink)`,
    macAddress: `MAC 주소`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `랜덤 MAC 주소 생성`,
    generateRandomMacConfirmTitle: `MAC 주소를 교체하시겠습니까?`,
    generateRandomMacConfirmDescription: `랜덤 MAC 주소를 생성하면 현재 값이 교체됩니다. 계속하시겠습니까?`,
    generateRandomMacConfirmAction: `새 MAC 생성`,
    copyProvisioningMac: `프로비저닝 URL용 MAC 복사`,
    copyProvisioningMacHint: `소문자, 콜론 없이 복사 (프로비저닝 URL용)`,
    copyProvisioningMacCopied: `{{value}} 복사됨`,
    copyProvisioningMacFailed: `복사에 실패했습니다. 값을 직접 선택해 주세요.`,
    checkingUniqueness: `고유성 확인 중…`,
    macRequired: `MAC 주소가 필요합니다`,
    macInvalid: `MAC 주소가 유효하지 않습니다`,
    macAlreadyInUse: `MAC 주소가 이미 사용 중입니다`,
    modelRequired: `장치 모델이 필요합니다.`,
    warrantyExpiresAt: `보증 만료일`,
    source: `출처`,
    lastProvisionedTime: `마지막 프로비저닝 시간`,
    lastProvisionedFrom: `마지막 프로비저닝 출처`,
    assignedUser: `할당된 사용자`,
    assignedUserRequired: `할당된 사용자가 필요합니다.`,
    directory: `디렉토리`,
    directoryRequired: `디렉토리가 필요합니다.`,
    selectDirectory: `디렉토리 선택...`,
    primaryLineExtension: `기본 회선 내선`,
    noExtensionsForUser: `할당된 사용자에게 유효한 회선 내선이 없습니다. 저장하기 전에 다른 사용자를 선택하거나 회선 내선을 할당하세요.`,
    primaryLineExtensionRequired: `기본 회선 내선이 필요합니다.`,
    timeZone: `시간대`,
    stationName: `스테이션 이름`,
    stationNameRequired: `스테이션 이름이 필요합니다.`,
    stationNamePlaceholder: `예: 프런트 데스크`,
    enableDaylightSavings: `일광 절약 시간제 활성화`,
    cannotSaveTitle: `아직 저장할 수 없습니다`,
    buttonConfigurationInvalid: `저장하기 전에 잘못된 버튼 구성을 수정하세요.`,

    // Advanced section labels
    pages: `페이지`,
    lineKeysAvailable: `사용 가능한 회선 키`,
    selectModelFirst: `먼저 모델을 선택하세요...`,
    lineKeysHelper: `({{pages}} 페이지{{pageSuffix}} x {{keysPerPage}} 키 = 총 {{total}}개)`,
    allowProvisioning: `프로비저닝 허용`,
    sidecarEnabled: `사이드카 활성화`,
    transport: `전송`,
    jitter: `지터`,
    serverGroupHost: `서버 그룹 호스트`,
    natTraversal: `NAT 순회`,
    wifiEnabled: `WiFi 활성화`,
    wifiNetworkSsid: `WiFi 네트워크 SSID`,
    wifiSecurityMode: `WiFi 보안 모드`,
    wifiPassword: `WiFi 비밀번호`,
    acousticShieldEnabled: `음향 쉴드 활성화`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `장치 미리보기`,
    unavailable: `선택한 모델의 미리보기를 사용할 수 없습니다.`,
    emptyAlt: `빈 장치 미리보기`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `회선 및 버튼`,
    sidecarButtons: `사이드카 버튼`,
    lineButtonHelper_one: `{{modelName}}의 버튼 슬롯 {{count}}개. 1행은 항상 회선이며 기본 회선 내선을 따릅니다.`,

    lineButtonHelper_other: `{{modelName}}의 버튼 슬롯 {{count}}개. 1행은 항상 회선이며 기본 회선 내선을 따릅니다.`,
    sidecarButtonHelper_one: `사이드카 슬롯 {{count}}개.`,

    sidecarButtonHelper_other: `사이드카 슬롯 {{count}}개.`,
    noSlotsForModel: `이 모델에 사용 가능한 버튼 슬롯이 없습니다.`,
    selectModelForSlots: `버튼 슬롯을 보려면 장치 모델을 선택하세요.`,
    columnNumber: `#`,
    columnFunction: `기능`,
    columnValue: `값`,
    columnLabel: `레이블`,
    phoneNumberPlaceholder: `전화번호`,
    optionalLabelPlaceholder: `선택적 레이블`,
    extensionNone: `-- 없음 --`,

    // Button function options
    functionBlank: `비어있음`,
    functionLine: `회선`,
    functionRollover: `롤오버`,
    functionSpeedDial: `단축 다이얼`,
    functionPresence: `BLF / 상태`,
    functionParkingLot: `파킹 로트`,
    functionSharedVoicemail: `공유 음성 메일`,

    // Preset speed dial options
    presetMangoSupport: `Mango 지원`,
    presetLoginQueue: `큐 로그인`,
    presetLogoutQueue: `큐 로그아웃`,
    presetBlockCaller: `발신자 차단`,
    presetOpenClose: `열기/닫기`,
    presetPauseResumeRecording: `녹음 일시정지/재개`,
    presetAlternateVm: `대체 VM`,
    preset911History: `911 기록`,

    // Preset button labels
    presetLabelMangoSupport: `Mango Support`,
    presetLabelLoginQ: `큐 로그인`,
    presetLabelLogoutQ: `큐 로그아웃`,
    presetLabelBlock: `차단`,
    presetLabelOpenClose: `열기/닫기`,
    presetLabelPauseResume: `일시정지/재개`,
    presetLabelAltVm: `대체 VM`,
    presetLabel911Hist: `911 기록`,

    parkingLotOption: `파킹 로트 {{number}}`,

    multipleLineWarningTitle: `여러 개의 회선 버튼이 감지되었습니다`,
    multipleLineWarning: `여러 개의 회선 버튼을 추가하면 원치 않는 결과가 발생할 수 있습니다. 선택한 내선이 다른 회선 버튼에 할당되어 있지 않고 다른 장치에 구성되어 있지 않은지 확인하세요.`,
    multipleLineWarningProceed: `계속`,
    multipleLineWarningCancel: `취소`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `장치 복제`,
    description: `"{{sourceLabel}}"에서 새 장치를 생성합니다. 고유한 MAC 주소를 입력하세요. 모든 설정과 버튼이 새 장치에 복사됩니다.`,
    loadingSettings: `장치 설정 로딩 중…`,
    stationLabelPlaceholder: `장치의 복제본`,
    cloneButton: `장치 복제`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `장치 삭제`,
    confirmation: `"{{name}}"을(를) 삭제하시겠습니까? 이 작업은 취소할 수 없습니다.`,
    unnamedDevice: `이름 없는 장치`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `장치 일괄 편집`,
    description_one: `장치 {{count}}개를 업데이트합니다. 현재 값을 유지하려면 필드를 변경하지 않은 상태로 두세요.`,

    description_other: `장치 {{count}}개를 업데이트합니다. 현재 값을 유지하려면 필드를 변경하지 않은 상태로 두세요.`,
    transport: `전송`,
    natTraversal: `NAT 순회`,
    jitter: `지터`,
    timeZone: `시간대`,
    daylightSaving: `일광 절약 시간`,
    allowProvisioning: `프로비저닝 허용`,
    serverGroupHost: `서버 그룹 호스트`,
    saveButton_one: `장치 {{count}}개 저장`,

    saveButton_other: `장치 {{count}}개 저장`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `원하는 펌웨어 버전 설정`,
    description: `선택한 {{count}}개의 장치에 대해 원하는 펌웨어 버전을 설정합니다. 업데이트 후 구성이 다시 로드됩니다.`,
    label: `원하는 펌웨어 버전`,
    placeholder: `예: 1.2.3.4`,
    confirmButton: `설정 및 다시 로드`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `장치`,
    searchPlaceholder: `이름, 사용자, 내선 또는 MAC으로 검색...`,
    addDevice: `장치 추가`,
    actionsButton: `작업`,
    actionsWithCount: `작업 ({{count}}개 선택됨)`,
    massEditAction: `일괄 편집 ({{count}}개)`,
    reloadConfigAction: `구성 다시 로드 ({{count}}개)`,
    reloadConfigRebootAction: `구성 다시 로드 및 재부팅 ({{count}}개)`,
    setFirmwareAction: `원하는 펌웨어 설정 ({{count}}개)`,
    rebuildAll: `모두 다시 빌드`,
    rebuildAndReboot: `다시 빌드 및 재부팅`,
    rebuildAllConfirmTitle: `모든 장치 구성을 다시 빌드하시겠습니까?`,
    rebuildAllConfirmDescription: `이 PBX의 모든 장치 구성을 다시 빌드하려고 합니다. 계속하시겠습니까?`,
    rebuildAllConfirmAction: `모두 다시 빌드`,
    rebuildAndRebootConfirmTitle: `모든 장치를 다시 빌드하고 재부팅하시겠습니까?`,
    rebuildAndRebootConfirmDescription: `이 PBX의 모든 장치 구성을 다시 빌드하고 재부팅하려고 합니다. 계속하시겠습니까?`,
    rebuildAndRebootConfirmAction: `다시 빌드 및 재부팅`,
    pbxRequired: `장치 페이지에는 PBX가 필요합니다.`,
    failedToLoad: `장치 로드 실패`,
    noMatchingDevices: `검색 조건과 일치하는 장치가 없습니다`,

    // Column headers
    columnName: `이름`,
    columnPrimaryExt: `기본 내선`,
    columnUser: `사용자`,
    columnModel: `모델`,
    columnMacAddress: `MAC 주소`,
    columnFirmware: `펌웨어`,
    columnWarranty: `보증`,
    columnLastCheckIn: `마지막 체크인`,
    columnInitializedOn: `초기화 날짜`,
    columnConfiguration: `구성`,
    columnActions: `작업`,
    selectAllDevices: `모든 장치 선택`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `장치 로딩 중...`,
    noDevices: `장치 없음`,
    noDevicesDescription: `이 PBX에 대한 장치가 아직 없습니다.`,
    selectDevice: `{{name}} 선택`,
    editDevice: `{{name}} 편집`,
    modelFallback: `모델 {{id}}`,
    reportsModel: `보고: {{model}}`,
    warrantyInWarranty: `보증 기간 내`,
    warrantyExpired: `만료됨`,
    warrantyUnknown: `보증 없음`,
    changesPending: `변경 사항 대기 중`,
    sidecar: `사이드카`,
    noProv: `프로비저닝 없음`,
    fromSource: `{{source}}에서`,
    desiredFirmware: `(원하는 버전: {{version}})`,
    firmwareUnknown: `알 수 없음`,
    rowActions: `행 작업`,
    cloneAction: `복제`,
    deleteAction: `삭제`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `제조사`,
    model: `모델`,
    firmware: `펌웨어`,
    firmwarePlaceholder: `전체`,
    warranty: `보증`,
    transport: `전송`,
    jitter: `지터`,
    sidecar: `사이드카`,
    provisioning: `프로비저닝`,
    clearFilters: `필터 지우기`,
    warrantyUnknown: `보증 없음`,
    warrantyInWarranty: `보증 기간 내`,
    warrantyExpired: `만료됨`,
    provisioningEnabled: `활성화`,
    provisioningDisabled: `비활성화`,
    provisioningChangesPending: `변경 사항 대기 중`,
    toggleFilters: `필터 토글`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `페이지당 결과:`,
    totalCount: `(총 {{count}}개)`,
    previousPage: `이전 페이지`,
    nextPage: `다음 페이지`,
    all: `전체`,
    jumpToPage: `{{page}}페이지로 이동`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `장치가 생성되었습니다`,
    createFailed: `장치 생성 실패`,
    deviceUpdated: `장치가 업데이트되었습니다`,
    deviceUpdatedRebooting: `장치가 업데이트되고 재부팅 중입니다`,
    updateFailed: `장치 업데이트 실패`,
  },
} as const satisfies DevicesLocale
